/**
 * Website block-DSL (model-facing contract) — v1 MVP + full-vocab section kinds.
 *
 * The AI emits this compact, ID-free JSON. The deterministic compiler
 * (`buildWebsiteCraftJsFromBlocks.ts`) turns it into a real website-builder
 * Craft.js tree by reusing the builder's own seed factories (`temp.ts`), so the
 * output is a normal, editable builder site — not raw HTML.
 *
 * All user-visible text is `LangText` (a bare string OR a per-locale map). This
 * mirrors how the runtime resolves per-field language maps (`resolve-text.ts`).
 *
 * React-free — shared by the client tool handler and the unit tests.
 */
import type { LangText } from "@/lib/utils/resolve-text";

/** Declarative scroll-reveal for a section. Absent/"none" = no motion and NO
 *  prop emitted (old docs stay byte-identical). */
export type WebsiteReveal = "none" | "fade" | "rise";

/** Theme-palette background token, resolved by the compiler to a concrete
 *  backgroundColor: surface = palette background, tint = a light wash of the
 *  primary color, contrast = the secondary color band. An explicit
 *  `backgroundColor` on the section always wins over this token. */
export type WebsiteBackgroundToken = "surface" | "tint" | "contrast";

/** A clickable link on a button: external https `url` OR internal `target`
 *  (an in-page anchor like "#register" or a page slug like "/login"). */
export type WebsiteButtonSpec = {
  text: LangText;
  url?: string;
  target?: string;
  /** Visual style. Absent = builder default (no prop emitted). */
  variant?: "solid" | "pill" | "ghost" | "elevated";
};

/** A field to collect in a register / datainput form. A bare string = bind the
 *  event field of that NAME with sensible defaults; the object form lets the AI
 *  honor specifics (label override, required, full-width). The `name` must be a
 *  real event guest field (the AI can create one first via create_additional_field). */
export type RegisterFieldSpec =
  | string
  | {
      /** event guest field NAME */
      name: string;
      label?: LangText;
      required?: boolean;
      /** span the full row instead of half */
      fullWidth?: boolean;
    };

/** A consent/opt-in checkbox in a datainput form. */
export type WebsiteCheckboxSpec = {
  label: LangText;
  required?: boolean;
  /** the boolean event field it writes (e.g. "emailOptIn"); absent/empty =
   *  a pure acknowledgement that stores nothing. A REQUIRED checkbox never
   *  writes "emailOptIn" — the compiler drops that binding (GDPR Art. 7(4)). */
  field?: string;
};

/** v1 MVP section kinds. Phase 3 extends `kind` with the full vocabulary. */
export type WebsiteSection =
  | {
      kind: "hero";
      headline?: LangText;
      header: LangText;
      text?: LangText;
      primaryButton?: WebsiteButtonSpec;
      secondaryButton?: WebsiteButtonSpec;
      imageSrc?: string;
      /** background | left | right | none */
      imageLayout?: "background" | "left" | "right" | "none";
      backgroundColor?: string;
      /** 0 | 60 | 70 | 80 | 90 | 100 */
      sectionHeight?: number;
      /** darkening scrim over the background media (bottom-anchored gradient) */
      overlay?: "none" | "scrim" | "scrim-strong";
      /** https background video (takes over the hero background) */
      videoSrc?: string;
      /** https poster shown before the video loads */
      videoPoster?: string;
      background?: WebsiteBackgroundToken;
      reveal?: WebsiteReveal;
    }
  | {
      kind: "text";
      headline?: LangText;
      header: LangText;
      text: LangText;
      /** "100%" | "75%" | "50%" | "33%" | "25%" */
      layoutWidth?: string;
      backgroundColor?: string;
      background?: WebsiteBackgroundToken;
      reveal?: WebsiteReveal;
    }
  | {
      kind: "image";
      headline?: LangText;
      header?: LangText;
      text?: LangText;
      imageSrc: string;
      imageAspectRatio?: string;
      /** flip the image to the left (text right) */
      inversed?: boolean;
      reveal?: WebsiteReveal;
    }
  | {
      kind: "register";
      headline?: LangText;
      header?: LangText;
      text?: LangText;
      /** fields to collect — names or per-field specs (validated ⊆ real guest fields) */
      fields: RegisterFieldSpec[];
      submitText?: LangText;
      reveal?: WebsiteReveal;
    }
  | {
      /** Post-acceptance data collection — the builder's multi-step DataInput
       *  (field groups + optional companions + consent checkboxes + overview).
       *  Shown to ACCEPTED guests. Use `register` for public signup. */
      kind: "datainput";
      headline?: LangText;
      header?: LangText;
      text?: LangText;
      /** one entry per step; a single step = a normal (non-stepped) form. Max 5. */
      steps: { label?: LangText; fields: RegisterFieldSpec[] }[];
      /** include a companion (plus-one) sub-form */
      companions?: boolean;
      /** consent / opt-in checkboxes */
      checkboxes?: WebsiteCheckboxSpec[];
      submitText?: LangText;
      /** allow already-submitted guests to edit their answers */
      allowUpdate?: boolean;
      reveal?: WebsiteReveal;
    }
  // ── Phase 3 full vocabulary ────────────────────────────────────────────────
  | {
      kind: "agenda";
      header: LangText;
      text?: LangText;
      items: { time?: LangText; title: LangText; speaker?: LangText; desc?: LangText }[];
      reveal?: WebsiteReveal;
    }
  | {
      kind: "speaker";
      header: LangText;
      text?: LangText;
      items: { name: LangText; occupation?: LangText; imageUrl?: string }[];
      reveal?: WebsiteReveal;
    }
  | {
      kind: "testimonials";
      header: LangText;
      text?: LangText;
      items: { quote: LangText; name: LangText; occupation?: LangText; imageUrl?: string }[];
      reveal?: WebsiteReveal;
    }
  | {
      kind: "numbers";
      header: LangText;
      text?: LangText;
      items: { number: LangText; label: LangText }[];
      reveal?: WebsiteReveal;
    }
  | {
      kind: "faq";
      header: LangText;
      text?: LangText;
      items: { question: LangText; answer: LangText }[];
      reveal?: WebsiteReveal;
    }
  | {
      kind: "gallery";
      header?: LangText;
      text?: LangText;
      /** https image URLs */
      images: string[];
      reveal?: WebsiteReveal;
    }
  | {
      kind: "countdown";
      header: LangText;
      text?: LangText;
      reveal?: WebsiteReveal;
    }
  | {
      kind: "map";
      header: LangText;
      text?: LangText;
      /** address / place searched by the map embed */
      location?: LangText;
      reveal?: WebsiteReveal;
    }
  | {
      kind: "login";
      header?: LangText;
      text?: LangText;
      submitText?: LangText;
      reveal?: WebsiteReveal;
    }
  | {
      kind: "sponsors";
      /** Logo tiles (SponsorsChild nodes): src https logo, href link, alt name.
       *  NOTE: no `reveal` here — there is no buildSponsors in the compiler, so
       *  a reveal on this member could never compile (dead capability). */
      items: { src: string; href?: string; alt?: string }[];
    };

export type WebsiteDslPage = {
  slug: string;
  pageName: string;
  sections: WebsiteSection[];
};

export type WebsiteThemeBlock = {
  /** WebsiteThemeId from themes.ts — optional palette/font base. */
  preset?: string;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  headerFont?: string;
  bodyFont?: string;
  rounding?: string;
  /** sanitized (script/expression-stripped) on save */
  customCss?: string;
  /** start the shared AppBar in transparent (over-hero) mode */
  appBarTransparent?: boolean;
  /** "display" = oversized fluid hero header (text-7xl + viewport clamp);
   *  absent/"regular" = the compiler's stock hero sizing. */
  displayScale?: "regular" | "display";
};

export type WebsiteDsl = {
  theme?: WebsiteThemeBlock;
  /** full BCP-47 primary locale, e.g. "de-DE" */
  primaryLanguage?: string;
  /** additional locales beyond primaryLanguage */
  languages?: string[];
  pages: WebsiteDslPage[];
};

// ── Caps (token/DOM bounds, mirror backend schema limits) ────────────────────
export const MAX_PAGES = 5;
export const MAX_SECTIONS_PER_PAGE = 20;
export const MAX_REGISTER_FIELDS = 20;

export type WebsiteCompileErrorCode =
  | "noPages"
  | "tooManyPages"
  | "noSections"
  | "tooManySections"
  | "unknownSectionKind"
  | "missingText"
  | "unknownLocale"
  | "invalidColor"
  | "invalidUrl"
  | "invalidSlug"
  | "noRegisterFields"
  | "tooManyRegisterFields"
  | "unknownRegisterField"
  | "noItems"
  | "tooManyItems"
  | "noSteps"
  | "tooManySteps"
  | "invalidStructure";

export const MAX_SECTION_ITEMS = 12;
export const MAX_DATAINPUT_STEPS = 5;

export type WebsiteCompileError = {
  ok: false;
  errorCode: WebsiteCompileErrorCode;
  detail?: string;
};
